import type { ImportDataUploadResult } from "@repo/types";
import { Formik } from "formik";
import { Label } from "@repo/ui/components/Base/Label";
import { FileUpload } from "@repo/ui/components/Base/FileUpload";
import { Button } from "@repo/ui/components/Base/Button";
import { FileDisplay } from "@repo/ui/components/Base/FileDisplay";
import { useState } from "react";
import { userImportService } from "@/services/userImportService";
import { DownloadIcon } from "lucide-react";
import { downloadFile } from "@repo/shared/utils";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { useGetUserImportExample } from "@/hooks/queryHooks/configHooks";

export interface UserImportUploadStepFormProps {
  importedFile: ImportDataUploadResult | null;
  tenantId: string;
  onCancel: () => void;
  onNext: () => void;
  setImportedFile: (importedFile: ImportDataUploadResult | null) => void;
  setHeaders: (headers: string[]) => void;
}

const maxFileSizeKb = 10240;
const fileTypeErrorMessage =
  "Invalid file type. Please upload a file with a .csv extension.";
const fileSizeErrorMessage = "File size must not exceed 10MB.";
const fileServerError = "File not processed: invalid size or structure";

export const UserImportUploadStepForm = ({
  importedFile,
  tenantId,
  onCancel,
  setImportedFile,
  setHeaders,
  onNext,
}: UserImportUploadStepFormProps) => {
  const [fileErrorMessage, setFileErrorMessage] = useState<string>("");
  const { data: example } = useGetUserImportExample();
  const onDownload = () => {
    if (example) {
      downloadFile(example, example.name);
    }
  };
  return (
    <Formik
      initialValues={
        {
          file: null,
          tenantId,
          objectName: "User",
        } as { file: File | null; tenantId: string; objectName: string }
      }
      async
      onSubmit={async (values) => {
        if (values.file && !importedFile) {
          const createdFile = await userImportService.uploadFile(
            values.file,
            values.tenantId,
            values.objectName
          );
          if (!createdFile.success) {
            setFileErrorMessage(fileServerError);
            return;
          }
          setImportedFile(createdFile.file);
          setHeaders(createdFile.headers?.filter((header) => !!header) ?? []);
        }
        onNext();
      }}
    >
      {({
        values,
        handleSubmit,
        setFieldValue,
        setTouched,
        touched,
        isSubmitting,
      }) => {
        return (
          <form onSubmit={handleSubmit}>
            <DialogBody className="flex flex-col gap-y-4">
              <div className="flex flex-col">
                <Label label="*User CSV File" id="file" className="py-3" />
                {!values.file && !importedFile && (
                  <FileUpload
                    id="file"
                    accept="all"
                    handleFileUpload={(file: File) => {
                      setTouched({ ...touched, file: true });
                      if (file.type !== "text/csv") {
                        setFileErrorMessage(fileTypeErrorMessage);
                        return;
                      }
                      if (file.size > maxFileSizeKb) {
                        setFileErrorMessage(fileSizeErrorMessage);
                        return;
                      }
                      setFieldValue("file", file);
                      setFileErrorMessage("");
                    }}
                    error={!!fileErrorMessage && touched.file}
                    errorMessage={fileErrorMessage}
                  />
                )}
                {!!values.file && !importedFile && (
                  <FileDisplay
                    name={values.file.name}
                    size={values.file.size}
                    disabled={isSubmitting}
                    onRemove={() => {
                      setFieldValue("file", undefined);
                      setFileErrorMessage("");
                    }}
                    error={!!fileErrorMessage && touched.file}
                    errorMessage={fileErrorMessage}
                  />
                )}
                {!!importedFile && (
                  <FileDisplay
                    name={importedFile.name}
                    size={importedFile?.sizeKb ?? 0}
                    disabled={isSubmitting}
                    onRemove={() => {
                      setImportedFile(null);
                      setFieldValue("file", undefined);
                      setFileErrorMessage("");
                    }}
                    error={!!fileErrorMessage && touched.file}
                    errorMessage={fileErrorMessage}
                  />
                )}
              </div>
              {!!example && (
                <div className="flex flex-row justify-end">
                  <button className="w-min" onClick={onDownload} type="button">
                    <div className="flex flex-row justify-start gap-2">
                      example.csv <DownloadIcon />
                    </div>
                  </button>
                </div>
              )}
            </DialogBody>
            <DialogFooter className="flex flex-row justify-between pt-0">
              <Button
                variant="destructive"
                type="button"
                disabled={isSubmitting}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  isSubmitting ||
                  (!values.file && !importedFile) ||
                  !!fileErrorMessage
                }
              >
                Next
              </Button>
            </DialogFooter>
          </form>
        );
      }}
    </Formik>
  );
};
